import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FolderLock, Trash2, Archive, FolderOpen, Landmark, Briefcase, FileQuestion } from 'lucide-react';
import { dataRoomAPI, investmentAPI, fundAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const StatusPill = ({ status }) => {
  const map = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    archived: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${map[status] || map.active}`}>
      {status === 'active' ? 'Active' : 'Archived'}
    </span>
  );
};

export default function DataRooms() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [investments, setInvestments] = useState({});
  const [funds, setFunds] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [linkTypeFilter, setLinkTypeFilter] = useState('');
  const [investmentFilter, setInvestmentFilter] = useState('');
  const [fundFilter, setFundFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    load();
  }, [linkTypeFilter, investmentFilter, fundFilter, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const [roomRes, invRes, fundRes] = await Promise.all([
        dataRoomAPI.list({
          link_type: linkTypeFilter || undefined,
          investment_id: investmentFilter || undefined,
          fund_id: fundFilter || undefined,
          status: statusFilter || undefined,
        }),
        investmentAPI.getAll(),
        fundAPI.getAll(),
      ]);
      const invMap = {};
      (invRes.data || []).forEach(i => { invMap[i.id] = i; });
      setInvestments(invMap);
      const fMap = {};
      (fundRes.data || []).forEach(f => { fMap[f.id] = f; });
      setFunds(fMap);
      setRooms(roomRes.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this data room and all its contents? This cannot be undone.')) return;
    try {
      await dataRoomAPI.remove(id);
      setRooms(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleArchive = async (id, newStatus, e) => {
    e.stopPropagation();
    try {
      await dataRoomAPI.update(id, { status: newStatus });
      load();
    } catch {
      alert('Failed to update');
    }
  };

  const filtered = rooms.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(s) || (r.description || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="p-8 text-center text-gray-500">Loading data rooms...</div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="data-rooms-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Data Rooms</h1>
          <p className="text-sm text-gray-500 mt-1">Secure virtual data rooms — scope each to a Fund, an Investment/Deal, or another purpose</p>
        </div>
        {canEdit && (
          <button
            onClick={() => navigate('/data-rooms/create')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 shadow-sm"
            data-testid="create-data-room-btn"
          >
            <Plus size={16} className="mr-2" /> Create Data Room
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or description..."
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            data-testid="search-input"
          />
        </div>
        <select
          value={linkTypeFilter}
          onChange={e => setLinkTypeFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
          data-testid="link-type-filter"
        >
          <option value="">All Types</option>
          <option value="fund">Fund</option>
          <option value="investment">Investment / Deal</option>
          <option value="other">Others</option>
        </select>
        {(!linkTypeFilter || linkTypeFilter === 'fund') && (
          <select
            value={fundFilter}
            onChange={e => setFundFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm min-w-[180px]"
            data-testid="fund-filter"
          >
            <option value="">All Funds</option>
            {Object.values(funds).map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        )}
        {(!linkTypeFilter || linkTypeFilter === 'investment') && (
          <select
            value={investmentFilter}
            onChange={e => setInvestmentFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm min-w-[180px]"
            data-testid="investment-filter"
          >
            <option value="">All Investments</option>
            {Object.values(investments).map(i => (
              <option key={i.id} value={i.id}>{i.portfolio_company_name}</option>
            ))}
          </select>
        )}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Data Rooms</p>
          <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="total-rooms">{rooms.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Active</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{rooms.filter(r => r.status === 'active').length}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Total Files</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{rooms.reduce((a, r) => a + (r.file_count || 0), 0)}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Invited Users</p>
          <p className="text-2xl font-bold text-indigo-600 mt-1">{rooms.reduce((a, r) => a + (r.external_user_count || 0), 0)}</p>
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-lg">
          <FolderLock size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No data rooms found.</p>
          {canEdit && (
            <button onClick={() => navigate('/data-rooms/create')} className="mt-3 text-sm text-blue-600 hover:underline">
              Create your first data room
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(r => {
            const linkType = r.link_type || (r.investment_id ? 'investment' : 'other');
            const linkLabel =
              linkType === 'fund'
                ? (r.fund_name || funds[r.fund_id]?.name || 'Fund')
                : linkType === 'investment'
                ? (r.investment_name || investments[r.investment_id]?.portfolio_company_name || 'Investment')
                : (r.custom_reason || 'Other');
            const LinkIcon = linkType === 'fund' ? Landmark : linkType === 'investment' ? Briefcase : FileQuestion;
            const linkBadgeColor =
              linkType === 'fund'
                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                : linkType === 'investment'
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-amber-50 text-amber-700 border-amber-200';
            return (
              <div
                key={r.id}
                onClick={() => navigate(`/data-rooms/${r.id}`)}
                className="bg-white border rounded-lg p-5 hover:shadow-md hover:border-blue-300 cursor-pointer transition"
                data-testid={`room-card-${r.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FolderLock size={20} className="text-blue-600" />
                  </div>
                  <StatusPill status={r.status} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 truncate">{r.name}</h3>
                <div className="mb-3 min-h-[32px]">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${linkBadgeColor}`}
                    data-testid={`link-badge-${r.id}`}
                  >
                    <LinkIcon size={10} />
                    {linkType === 'fund' ? 'Fund' : linkType === 'investment' ? 'Investment' : 'Other'}
                  </span>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{linkLabel}</p>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t">
                  <span><FolderOpen size={12} className="inline mr-1" />{r.folder_count || 0} folders</span>
                  <span>{r.file_count || 0} files</span>
                  <span>{r.external_user_count || 0} users</span>
                </div>
                <div className="flex gap-2 mt-3">
                  {canEdit && (
                    <button
                      onClick={e => handleArchive(r.id, r.status === 'active' ? 'archived' : 'active', e)}
                      className="text-xs text-gray-500 hover:text-gray-700"
                      data-testid={`archive-${r.id}`}
                    >
                      <Archive size={12} className="inline mr-1" />
                      {r.status === 'active' ? 'Archive' : 'Unarchive'}
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={e => handleDelete(r.id, e)}
                      className="text-xs text-red-500 hover:text-red-700 ml-auto"
                      data-testid={`delete-${r.id}`}
                    >
                      <Trash2 size={12} className="inline mr-1" /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
